// The chart's data: the probes' series aligned on one time axis, for uPlot (kept apart from the
// component so that it can be tested without a DOM).

import type { SeriesPoint, SeriesResponse } from '@cck/schema';

export type Y = number | null | undefined;

export interface ChartData {
  x: number[];
  /** Per probe: average (or raw value), min and max (rollups). `undefined`: no point there; `null`: a gap. */
  lines: { probeId: string; avg: Y[]; min: Y[]; max: Y[] }[];
  rollup: boolean;
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)]! : 0;
};

/** Aligns the probes' points on one time axis, with a gap where a probe's data stops for a while. */
export function buildChartData(res: SeriesResponse): ChartData {
  const rollup = res.tier !== 'raw';
  const perProbe = res.series.map((s) => {
    const ts = s.points.map((p) => Date.parse(p[0]) / 1000);
    const steps = ts.slice(1).map((t, i) => t - ts[i]!);
    const expected = res.bucket_s ?? (res.tier === 'local_day' ? 86_400 : Math.max(60, median(steps)));
    const gapAfter = rollup ? expected * 2.5 : Math.max(expected * 3, 600);
    const points: { t: number; v: [number, number, number] | null }[] = [];
    s.points.forEach((p: SeriesPoint, i) => {
      if (i > 0 && ts[i]! - ts[i - 1]! > gapAfter) points.push({ t: (ts[i]! + ts[i - 1]!) / 2, v: null });
      const [avg, min, max] = p.length === 2 ? [p[1], p[1], p[1]] : [p[4], p[2], p[3]];
      points.push({ t: ts[i]!, v: [avg as number, min as number, max as number] });
    });
    return { probeId: s.probe_id, points };
  });
  const x = [...new Set(perProbe.flatMap((p) => p.points.map((q) => q.t)))].sort((a, b) => a - b);
  const index = new Map(x.map((t, i) => [t, i]));
  const lines = perProbe.map((p) => {
    const avg: Y[] = new Array(x.length).fill(undefined);
    const min: Y[] = new Array(x.length).fill(undefined);
    const max: Y[] = new Array(x.length).fill(undefined);
    for (const q of p.points) {
      const i = index.get(q.t)!;
      avg[i] = q.v ? q.v[0] : null;
      min[i] = q.v ? q.v[1] : null;
      max[i] = q.v ? q.v[2] : null;
    }
    return { probeId: p.probeId, avg, min, max };
  });
  return { x, lines, rollup };
}

/** Decimals a tick increment needs: 5 → 0, 2.5 → 1, 0.25 → 2. */
export function decimalsOf(incr: number): number {
  for (let d = 0; d < 3; d++) if (Math.abs(Math.round(incr * 10 ** d) - incr * 10 ** d) < 1e-9) return d;
  return 3;
}
